import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router, UrlSegment } from '@angular/router';
import { Subject } from 'rxjs';
import { MenuItemType } from '../nav/nav.component';

interface UrlRoute {
	id?: string;
	url: UrlSegment[] | string | string[];
	queryParams: any;
	name?: string;
	menuItemType?: MenuItemType;
}

interface SimplifiedRoute {
	url: string[];
	queryParams: any;
	name: string;
	menuItemType?: MenuItemType;
}

const SESSION_STORAGE_KEY_URLHISTORY = 'slUrlHistory';
const SESSION_STORAGE_LAST_NAV_ITEM_SELECTED = 'slLastNavItemSelected';
export const BREADCRUMB_UNIQUE_QUERYPARAM = 'breadcrumbUnique';

@Injectable({
	providedIn: 'root',
})
export class NavigationHistoryService {
	// TODO: Might be worthwhile to move `urlHistory` to `currentUrlHistory` and also
	//       have `allUrlHistory` - then when back button is clicked, load from
	//       `allUrlHistory` if it goes back further then `currentUrlHistory`. This
	//       could allow retaining breadcrumbs when navigating back to a page that
	//       is from a different main tab.
	private urlHistory: UrlRoute[] = [];
	private mainNavType?: MenuItemType;

	public changesOccurred = new Subject<UrlRoute>();
	public navigationItemChangeOccurred = new Subject<MenuItemType>();

	constructor(
		private readonly router: Router,
		private readonly location: PlatformLocation
	) {
		// When browser's back button clicked...
		location.onPopState((a) => {
			const url = this.location.href;
			const routeItem = this.getNavItemForRoute(url);
			this.clearHistoryTo(url, 0);
			if (routeItem !== undefined) {
				this.setLastNavigationItemClicked(routeItem);
				this.navigationItemChangeOccurred.next(routeItem);
			}
		});
	}

	private loadUrlHistory(): void {
		try {
			const history = sessionStorage.getItem(SESSION_STORAGE_KEY_URLHISTORY);
			if (history) {
				this.urlHistory = JSON.parse(history);
			} else {
				this.urlHistory = [];
			}
		} catch {
			this.urlHistory = [];
		}
	}

	private saveUrlHistory(): void {
		sessionStorage.setItem(
			SESSION_STORAGE_KEY_URLHISTORY,
			JSON.stringify(this.urlHistory.map((r) => this.getSimplifiedRoute(r)))
		);
	}

	public clearHistory(keep: number | undefined = undefined): void {
		if (keep === undefined) {
			keep = 1;
		}
		if (keep === 0) {
			this.urlHistory = [];
		} else {
			this.urlHistory.splice(0, this.urlHistory.length - keep);
		}
		this.saveUrlHistory();
	}

	public clearHistoryTo(url: string, defaultToKeep: number): void {
		let savedIndex = -1;
		for (let index = this.urlHistory.length - 1; index >= 0; index--) {
			const cur = this.urlHistory[index];
			if (this.routeStringsMatch(this.serializeRoute(cur), url)) {
				savedIndex = index;
			}
		}

		if (savedIndex < 0) {
			savedIndex = defaultToKeep;
		}

		if (savedIndex >= 0) {
			this.clearHistory(savedIndex);
		}
	}

	private serializeRoute(route: UrlRoute): string {
		let url = '';
		let queryParams: any = {};
		if (Array.isArray(route.url)) {
			url = route.url.map((r) => (r as any).path ?? r.toString()).join('/');
			queryParams = route.queryParams;
		} else {
			url = route.url;
			// If URL has queryparams, move them to the queryParams object
			if (url.indexOf('?') >= 0) {
				const urlParts = url.split('?');
				url = urlParts[0];
				const qs = urlParts[1];

				const kvps = qs.split('&');
				kvps.forEach((pair) => {
					const kvpParts = pair.split('=');
					queryParams[kvpParts[0]] = kvpParts[1];
				});
			}
		}

		if (route.queryParams) {
			let params = '';
			for (const key in route.queryParams) {
				if (key !== BREADCRUMB_UNIQUE_QUERYPARAM) {
					params += `${key}=${route.queryParams[key]}`;
				} else {
				}
			}
			if (params) {
				url += `?${params}`;
			}
		}
		url = `/${url}`.replace(/\/\//g, '/');
		return url;
	}

	private routeStringsMatch(r1: string, r2: string): boolean {
		if (r1 === r2) {
			return true;
		}

		if (r1.indexOf('?') < 0) {
			r1 = r1 + '?tabIndex=0';
		}
		if (r2.indexOf('?') < 0) {
			r2 = r2 + '?tabIndex=0';
		}
		return r1 === r2;
	}

	private routesMatch(r1: UrlRoute, r2: UrlRoute): boolean {
		let sr1 = this.serializeRoute(r1);
		let sr2 = this.serializeRoute(r2);
		return this.routeStringsMatch(sr1, sr2);
	}

	private extractQueryParams = (
		url: string
	): { url: string; queryParams: any } => {
		const queryParams: any = {};
		if (url.indexOf('?') >= 0) {
			const urlParts = url.split('?');
			url = urlParts[0];
			const qs = urlParts[1];

			const kvps = qs.split('&');
			kvps.forEach((pair) => {
				const kvpParts = pair.split('=');
				queryParams[kvpParts[0]] = kvpParts[1];
			});
		}

		return { url, queryParams };
	};

	private getSimplifiedRoute(route: UrlRoute): SimplifiedRoute {
		let queryParams: any = route.queryParams;
		let url = route.url;
		if (!Array.isArray(url)) {
			// If URL has queryparams, move them to the queryParams object
			const xqp = this.extractQueryParams(url);
			url = [xqp.url];
			queryParams = { ...queryParams, ...xqp.queryParams };
		}

		const newUrl: string[] = [];
		for (const part of url) {
			newUrl.push(part.toString());
		}

		const simpRoute: SimplifiedRoute = {
			url: newUrl,
			queryParams,
			name: route.name ?? '',
			menuItemType: route.menuItemType,
		};

		return simpRoute;
	}

	public addRoute(route: UrlRoute): void {
		if (this.urlHistory.length === 0) {
			this.loadUrlHistory();
		}

		if (route.menuItemType === undefined) {
			route.menuItemType = this.mainNavType;
		}

		// If the route URL is a string, extract any query params from it
		if (!Array.isArray(route.url)) {
			const xqp = this.extractQueryParams(route.url);
			route.url = xqp.url;
			route.queryParams = { ...route.queryParams, ...xqp.queryParams };
		}

		// If router we're adding already exists, go back to it
		const existingIndex = this.urlHistory.findIndex((r) =>
			this.routesMatch(r, route)
		);
		if (existingIndex >= 0) {
			this.urlHistory.splice(existingIndex + 1);
		} else {
			// Otherwise, add it to the history
			this.urlHistory.push(route);
		}

		this.changesOccurred.next(route);
	}

	public getLastRoutes(count: number = 1): UrlRoute[] {
		if (this.urlHistory.length < 1) {
			this.loadUrlHistory();
		}

		// Only take the last item for a given name in the routes
		const namedRoutes = this.urlHistory.filter((route) => !!route.name);
		const routes: UrlRoute[] = [];
		for (let index = 0; index < namedRoutes.length; index++) {
			const cur = namedRoutes[index];
			if (namedRoutes.length > index + 1) {
				if (namedRoutes[index + 1]?.name !== cur.name) {
					routes.push(cur);
				}
			} else {
				routes.push(cur);
			}
		}

		return routes.slice(-count);
	}

	public getLastSimplifiedRoutes(count: number = 1): SimplifiedRoute[] {
		const routes = this.getLastRoutes(count);
		const simplifiedRoutes = routes.map((r) => this.getSimplifiedRoute(r));

		return simplifiedRoutes;
	}

	public async navigateBack(state?: any): Promise<void> {
		const lastRoutes = this.getLastSimplifiedRoutes(1);
		if (lastRoutes.length < 1) {
			console.error('trying to navigate back, but no URL in history');
			return;
		}

		let urlParts = lastRoutes[0].url;
		if (!Array.isArray(urlParts)) {
			urlParts = [urlParts];
		}

		const queryParams = lastRoutes[0].queryParams;

		await this.router.navigate(urlParts, { queryParams, state });
	}

	public setLastNavigationLabel(
		label: string,
		updateExisting: boolean = false
	): void {
		if (this.urlHistory.length === 0) {
			// No history to update
			console.warn('Trying to update label when no navigation exists');
			return;
		}
		if (updateExisting || !this.urlHistory[this.urlHistory.length - 1].name) {
			this.urlHistory[this.urlHistory.length - 1].name = label;
			this.changesOccurred.next(this.urlHistory[this.urlHistory.length - 1]);
			this.saveUrlHistory();
		}
	}

	public clearLastNavigationItemClicked(): void {
		this.setLastNavigationItemClicked();
	}

	public setLastNavigationItemClicked(
		itemType?: MenuItemType,
		updateExisting: boolean = false
	): void {
		this.mainNavType = itemType;

		if (this.mainNavType === undefined) {
			sessionStorage.removeItem(SESSION_STORAGE_LAST_NAV_ITEM_SELECTED);
			return;
		}

		if (
			this.urlHistory[this.urlHistory.length - 1] &&
			(updateExisting ||
				!this.urlHistory[this.urlHistory.length - 1].menuItemType)
		) {
			this.urlHistory[this.urlHistory.length - 1].menuItemType = itemType;
			this.changesOccurred.next(this.urlHistory[this.urlHistory.length - 1]);
			this.saveUrlHistory();
		}

		sessionStorage.setItem(
			SESSION_STORAGE_LAST_NAV_ITEM_SELECTED,
			String(itemType)
		);
	}

	public getNavItemForRoute(url: string): MenuItemType | undefined {
		url = url.replace(/https?:\/\//gi, '');

		for (let index = this.urlHistory.length - 1; index >= 0; index--) {
			const cur = this.urlHistory[index];
			if (this.routeStringsMatch(this.serializeRoute(cur), url)) {
				return cur.menuItemType;
			}
		}

		// MenuItemType not found in current history, so guess based on the URL
		const urlParts = url.split('/');
		if (urlParts.length < 2) {
			return undefined;
		}

		const topLevelNode = urlParts[1];
		switch (topLevelNode) {
			case 'company':
				return MenuItemType.companies;
			case 'users':
				return MenuItemType.users;
			case 'properties':
				return MenuItemType.properties;
			case 'devices':
				return MenuItemType.devices;
			case 'reports':
				return MenuItemType.reports;
			case 'roles':
				return MenuItemType.roles;
			case 'access':
				return MenuItemType.deviceAccess;
			default:
				return undefined;
		}
	}

	public getLastNavigationItemClicked(): MenuItemType | undefined {
		if (this.mainNavType === undefined) {
			this.mainNavType = Number(
				sessionStorage.getItem(SESSION_STORAGE_LAST_NAV_ITEM_SELECTED)
			);
		}
		return this.mainNavType;
	}
}
